import React, { useCallback, useEffect, useMemo } from 'react';
import { ReduxControls, ProgressIndicator, ErrorBanner } from 'rhombus-react';
import { CREATIVE_ASSET_TYPES } from 'utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { getFormValues, Field } from "redux-form";
import { noop } from "utils/common";
import { getAECompositions, clearCompositions } from "reducers/AfterEffectsCompositions";
import { useFlag } from "@innovid/feature-flags-react";
import { FEATURE_FLAGS } from "../../featureFlag/featureFlag";

import './AfterEffectsCompSelector.css';

export default function AfterEffectsCompSelector({
	input,
	meta,
	isEditMode,
	placeholder = "Select a composition to be used for this creative",
	...props
}) {
	const dispatch = useDispatch();

	const inputProps = {
		...input,
		...props,
		meta,
	};

	const { name, onChange, showPristineErrors } = input || {};
	const [adType] = name.split(".");
	const {
		doVersionsExist,
		creativeValues,
		compositions,
		isLoadingCompositions,
		errorObj,
		libraryId,
	} = useSelector((state) => {
		return {
			doVersionsExist: state.creativeDetails?.numberOfVersions > 1,
			creativeValues: getFormValues(meta.form)(state)?.[adType] || {},
			compositions: state.aeCompositions?.items,
			isLoadingCompositions: state.aeCompositions?.isLoading,
			errorObj: state.aeCompositions?.error,
			libraryId: state.libraryProperties?.id,
		};
	});

	let { value } = input;

	const { disabled } = inputProps;

	const isRadioDisabled = disabled || doVersionsExist;
	const isCreativeDynamic = creativeValues?.isDynamic;
	const assetType = creativeValues?.assetType;
	const aePackage = creativeValues.cards && creativeValues.cards[0].afterEffectsPackage || creativeValues.afterEffectsPackage;
	const packageId = aePackage?.id;
	const creativeAeCompositionId = value?.id;

	useEffect(() => {
		if (packageId) {
			dispatch(getAECompositions(libraryId, packageId));
		} else {
			dispatch(clearCompositions());
		}
	}, [libraryId, packageId, dispatch]);

	useEffect(() => {
		if (creativeAeCompositionId && compositions?.length > 0) {
			const matchedComp = compositions.find(comp => String(comp.id) === String(creativeAeCompositionId));
			if (matchedComp) {
				onChange({
					...value,
					id: matchedComp.id,
					name: matchedComp.name,
				});
			}
		}
	}, [creativeAeCompositionId, compositions, onChange, value]);

	const compositionOptions = useMemo(() => {
		return compositions?.map(comp => ({
			value: comp.id,
			label: comp.name,
		}));
	}, [compositions]);

	const onCompIdChange = useCallback((newValue) => {
		onChange({
			...value,
			id: newValue?.value,
			name: newValue?.label,
		});
	}, [onChange, value]);

	const isAESocialVersionsFlagEnabled = useFlag(FEATURE_FLAGS.AFTER_EFFECTS_SOCIAL_VERSIONS);

	if (!isAESocialVersionsFlagEnabled || !isCreativeDynamic || assetType !== CREATIVE_ASSET_TYPES.AFTER_EFFECTS) {
		return null;
	}

	return (
		<div className="full-width ae-comp-selector">
			<ProgressIndicator isLoading={isLoadingCompositions} />
			{!!Object.keys(errorObj||{})?.length && (
				<ErrorBanner
					title="Unable to load compositions."
					messages={[errorObj?.description]}
				/>
			)}
			<Field
				name={"aeCompositionId"}
				label="Select Composition from the After Effects Project"
				placeholder={placeholder}
				component={ReduxControls.Select}
				icon="search"
				required
				options={compositionOptions}
				disabled={isRadioDisabled}
				isLoading={isLoadingCompositions}
				isClearable={false}
				onMenuScrollToBottom={noop}
				showIcon
				onChange={onCompIdChange}
				format={() => {
					if (!value?.id) {
						return "";
					}
					return compositionOptions?.find(opt => String(opt.value) === String(value.id)) || "";
				}}
				value={value?.id ? compositionOptions?.find(opt => String(opt.value) === String(value.id)) || "" : ""}
				showPristineErrors={!!showPristineErrors}
			/>
			<div className="selected-project">
				<b>After Effects Project:</b> {aePackage?.name}
			</div>
		</div>
	);
}
AfterEffectsCompSelector.displayName = "AfterEffectsCompSelector";

export const afterEffectsComposition = {
	name: "afterEffectsComposition",
	label: "After Effects Composition",
	required: true,
	component: AfterEffectsCompSelector,
};

export const afterEffectsCompositionForCard = {
	...afterEffectsComposition,
	placeholder: "Select a composition to be used for this card",
};
